/**
 * WebsocketContext - Real-time updates via Action Cable WebSocket
 *
 * Features:
 * - Exponential backoff reconnection with jitter
 * - Connection state tracking via EventBus
 * - Full state sync on connect via /api/v2/sync
 * - All events published to EventBus for decoupled handling
 * - Online/offline detection
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "./UserContext.tsx";
import { OrganizationContext } from "./OrganizationContext.tsx";
import { getEventBus, EventTypes, EventType, EventPayloadMap, ConnectionState } from "../services/EventBus.ts";

import { BASE_URL } from "../api/index.ts";

// Derive WebSocket URL from BASE_URL (replace http(s) with ws(s) and remove /api/vX path)
const baseOrigin = new URL(BASE_URL).origin;
const WEBSOCKET_BASE_URL = baseOrigin.replace(/^http/, 'ws') + "/api/v2/cable";

// Reconnection configuration
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const JITTER_FACTOR = 0.25;

interface WebsocketContextValue {
  connectionState: ConnectionState;
  isOnline: boolean;
  reconnect: () => void;
}

const WebsocketContext = createContext<WebsocketContextValue>({
  connectionState: 'disconnected',
  isOnline: true,
  reconnect: () => {},
});

/**
 * Action Cable message types
 */
type ActionCableMessageType =
  | 'welcome'
  | 'ping'
  | 'pong'
  | 'confirm_subscription'
  | 'reject_subscription'
  | 'disconnect';

interface ActionCableMessage {
  type?: ActionCableMessageType;
  identifier?: string;
  message?: {
    event: string;
    payload: unknown;
  };
  reason?: string;
  reconnect?: boolean;
}

/**
 * Calculate exponential backoff with jitter
 */
function calculateBackoff(retryCount: number): number {
  const base = INITIAL_BACKOFF_MS * Math.pow(2, retryCount);
  const capped = Math.min(base, MAX_BACKOFF_MS);
  const jitter = capped * JITTER_FACTOR;
  return capped + (Math.random() * 2 - 1) * jitter;
}

/**
 * Fetch full state sync from server
 */
async function fetchSync(token: string): Promise<EventPayloadMap[typeof EventTypes.SYNC]> {
  const response = await fetch(`${BASE_URL}/sync`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Sync fetch failed: ${response.status} ${response.statusText}`);
  }

  return await response.json();
}

function WebsocketProvider({ children }: { children: ReactNode }) {
  const { user } = useContext(UserContext);
  const { currentOrganization } = useContext(OrganizationContext);
  const eventBus = getEventBus();

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectingRef = useRef(false);

  // Track current org ID in a ref so event handlers can access latest value without stale closure
  const currentOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    currentOrgIdRef.current = currentOrganization?.id ?? null;
  }, [currentOrganization?.id]);

  // Update EventBus connection state when local state changes
  useEffect(() => {
    eventBus.setConnectionState(connectionState);
  }, [connectionState, eventBus]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger reconnect when coming back online
      if (connectionState === 'disconnected' && user?.token) {
        connect();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectionState, user?.token]);

  // Close WebSocket cleanly before page unload to prevent "connection interrupted" errors
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Page unloading');
        wsRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle visibility change (tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && connectionState === 'disconnected' && user?.token) {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connectionState, user?.token]);

  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    const data = JSON.parse(event.data) as ActionCableMessage;

    // Handle Action Cable protocol messages
    if (data.type === 'ping' || data.type === 'pong' || data.type === 'welcome') {
      return;
    }

    if (data.type === 'confirm_subscription') {
      return;
    }

    if (data.type === 'reject_subscription') {
      throw new Error('WebSocket: Subscription rejected');
    }

    if (data.type === 'disconnect') {
      console.log('WebSocket: Server requested disconnect', data.reason);
      if (data.reconnect !== false) {
        scheduleReconnect();
      }
      return;
    }

    // Handle application messages
    if (!data.message) {
      throw new Error(`WebSocket: Unexpected message format - no message property: ${JSON.stringify(data)}`);
    }

    if (!data.message.event) {
      throw new Error(`WebSocket: Message missing event property: ${JSON.stringify(data)}`);
    }

    if (data.message.payload === undefined) {
      throw new Error(`WebSocket: Message missing payload for event ${data.message.event}: ${JSON.stringify(data)}`);
    }

    const eventType = data.message.event as EventType;
    const payload = data.message.payload as Record<string, unknown>;

    // These events should NOT be filtered by organization:
    // - Org membership events affect the user's org list across ALL their organizations
    // - Note type events can be personal (no org_id) or organizational
    // - User preference events (like default note type) apply to the user, not org
    const unfilterableEvents: EventType[] = [
      EventTypes.ORGANIZATION_UPDATED,
      EventTypes.MEMBER_ADDED,
      EventTypes.MEMBER_REMOVED,
      EventTypes.MEMBER_ROLE_CHANGED,
      EventTypes.INVITATION_CREATED,
      EventTypes.INVITATION_ACCEPTED,
      EventTypes.INVITATION_CANCELLED,
      EventTypes.NOTE_TYPE_CREATED,
      EventTypes.NOTE_TYPE_UPDATED,
      EventTypes.NOTE_TYPE_ARCHIVED,
      EventTypes.DEFAULT_NOTE_TYPE_CHANGED,
      EventTypes.DEFAULT_FOLLOWUP_NOTE_TYPE_CHANGED,
      EventTypes.USER_PREFERENCES_CHANGED,
    ];

    // Filter events by organization - if event has organization_id and it doesn't match current org, skip it
    // This prevents data from one org showing up when viewing another org
    // Exception: unfilterable events always pass through
    if (
      !unfilterableEvents.includes(eventType) &&
      payload &&
      typeof payload === 'object' &&
      'organization_id' in payload &&
      currentOrgIdRef.current !== null &&
      payload.organization_id !== currentOrgIdRef.current
    ) {
      // Event is for a different organization, ignore it
      return;
    }

    // Publish to EventBus for all subscribers
    eventBus.publish(eventType, payload as EventPayloadMap[typeof eventType]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus]);

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const backoff = calculateBackoff(retryCountRef.current);
    retryCountRef.current++;

    // Only log reconnection attempts after the first few (startup noise reduction)
    if (retryCountRef.current > 2) {
      console.log(`WebSocket: Reconnecting in ${Math.round(backoff)}ms (attempt ${retryCountRef.current})`);
    }
    setConnectionState('reconnecting');

    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, backoff);
  }, []);

  /**
   * Connect to WebSocket and fetch initial sync
   */
  const connect = useCallback(async () => {
    if (!user?.token || isConnectingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    isConnectingRef.current = true;
    setConnectionState('connecting');

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const wsUrl = `${WEBSOCKET_BASE_URL}?token=${encodeURIComponent(user.token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        retryCountRef.current = 0;
        isConnectingRef.current = false;

        // Subscribe to UserChannel
        ws.send(JSON.stringify({
          command: 'subscribe',
          identifier: JSON.stringify({ channel: 'UserChannel' })
        }));

        // Fetch full state sync - errors must be handled here since this is an event callback
        fetchSync(user.token!)
          .then(syncData => {
            eventBus.publish(EventTypes.SYNC, syncData);
            setConnectionState('connected');
          })
          .catch(error => {
            // Re-throw to trigger global error handler / Sentry
            throw error;
          });
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        wsRef.current = null;
        isConnectingRef.current = false;

        // 1000 = normal close, 1001 = going away (page unload/HMR), both are expected
        if (event.code === 1000 || event.code === 1001) {
          setConnectionState('disconnected');
          return;
        }

        // Only log unexpected closes
        console.log('WebSocket: Closed', event.code, event.reason);
        scheduleReconnect();
      };

      ws.onerror = () => {
        // Don't log errors - they're followed by onclose which handles reconnection
        isConnectingRef.current = false;
      };
    } catch (error) {
      console.error('WebSocket: Connection error', error);
      isConnectingRef.current = false;
      scheduleReconnect();
    }
  }, [user?.token, handleMessage, eventBus, scheduleReconnect]);

  /**
   * Manual reconnect trigger
   */
  const reconnect = useCallback(() => {
    retryCountRef.current = 0;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    connect();
  }, [connect]);

  // Connect when user token is available
  useEffect(() => {
    if (user?.token) {
      // Delay connection slightly to avoid race conditions during page refresh
      // This gives the browser time to complete the page load and settle
      const connectTimeout = setTimeout(() => {
        connect();
      }, 100);

      return () => {
        clearTimeout(connectTimeout);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        if (wsRef.current) {
          wsRef.current.close(1000, 'Component unmounting');
          wsRef.current = null;
        }
      };
    }
  }, [user?.token, connect]);

  return (
    <WebsocketContext.Provider value={{ connectionState, isOnline, reconnect }}>
      {children}
    </WebsocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket connection state
 */
function useWebsocket() {
  return useContext(WebsocketContext);
}

/**
 * Hook to access connection state from EventBus
 * Use this when you need connection state outside of React context
 */
function useConnectionState(): ConnectionState {
  const [state, setState] = useState<ConnectionState>(() => getEventBus().getConnectionState());

  useEffect(() => {
    const subscription = getEventBus().subscribeToConnectionState(setState);
    return () => subscription.unsubscribe();
  }, []);

  return state;
}

export { WebsocketContext, WebsocketProvider, useWebsocket, useConnectionState };
